/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, defineProperty as _defineProperty, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@carbon/react';
import { Edit, Save } from '@carbon/react/icons';
import classnames from 'classnames';
import { useTranslation } from '../../../../Utilities/i18n/i18n.js';

var _excluded = ["children", "cancelDisabled", "className", "editText", "editing", "editDisabled", "locale", "onCancelClick", "onEditClick", "onSaveClick", "saveText", "saveDisabled", "small", "subtitle", "subtitleComponent", "title", "titleComponent", "link", "linkElement", "linkProp", "linkProps", "linkText", "linkIcon"];
var CardHeaderActions = function CardHeaderActions(_ref) {
  var cancelDisabled = _ref.cancelDisabled,
    external = _ref.external,
    internal = _ref.internal,
    onCancelClick = _ref.onCancelClick,
    onCancelButtonClick = _ref.onCancelButtonClick,
    onSaveButtonClick = _ref.onSaveButtonClick,
    saveDisabled = _ref.saveDisabled,
    saveText = _ref.saveText,
    t = _ref.t;
  if ((internal || external) && onCancelClick) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
      kind: "ghost",
      className: "cpc--card__header-button",
      disabled: cancelDisabled,
      onClick: onCancelButtonClick
    }, t('Cancel')), /*#__PURE__*/React.createElement(Button, {
      kind: "ghost",
      className: "cpc--card__header-button cpc--card__header-button--save",
      disabled: saveDisabled,
      onClick: onSaveButtonClick
    }, saveText || t('Save'), /*#__PURE__*/React.createElement(Save, {
      className: "cds--btn__icon"
    })));
  }
  return /*#__PURE__*/React.createElement(Button, {
    kind: "ghost",
    className: "cpc--card__header-button",
    disabled: saveDisabled,
    onClick: onSaveButtonClick
  }, saveText || t('Save'), /*#__PURE__*/React.createElement(Save, {
    className: "cds--btn__icon"
  }));
};
var CardHeader = function CardHeader(_ref2) {
  var children = _ref2.children,
    cancelDisabled = _ref2.cancelDisabled,
    className = _ref2.className,
    editText = _ref2.editText,
    editing = _ref2.editing,
    editDisabled = _ref2.editDisabled;
    _ref2.locale;
    var onCancelClick = _ref2.onCancelClick,
    onEditClick = _ref2.onEditClick,
    onSaveClick = _ref2.onSaveClick,
    saveText = _ref2.saveText,
    saveDisabled = _ref2.saveDisabled,
    small = _ref2.small,
    subtitle = _ref2.subtitle,
    _ref2$subtitleCompone = _ref2.subtitleComponent,
    SubtitleComponent = _ref2$subtitleCompone === void 0 ? 'h4' : _ref2$subtitleCompone,
    title = _ref2.title,
    _ref2$titleComponent = _ref2.titleComponent,
    TitleComponent = _ref2$titleComponent === void 0 ? 'h3' : _ref2$titleComponent,
    link = _ref2.link,
    _ref2$linkElement = _ref2.linkElement,
    LinkElement = _ref2$linkElement === void 0 ? 'a' : _ref2$linkElement,
    _ref2$linkProp = _ref2.linkProp,
    linkProp = _ref2$linkProp === void 0 ? 'href' : _ref2$linkProp,
    linkProps = _ref2.linkProps,
    linkText = _ref2.linkText,
    linkIcon = _ref2.linkIcon,
    rest = _objectWithoutProperties(_ref2, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    internalEditing = _useState2[0],
    setInternalEditing = _useState2[1];

  // Whether or not to rely on external or internal state for editing
  var external = onSaveClick && typeof editing === 'boolean';
  var internal = onEditClick && onSaveClick && typeof editing !== 'boolean';
  var isEditing = external ? editing : internalEditing;
  // Whether the edit or save buttons should be displayed
  var showButtons = isEditing || internal || external && !children;
  var onEditButtonClick = function onEditButtonClick(event) {
    // If editing is not provided, use internal state
    if (internal && !internalEditing) {
      onEditClick(event);
      setInternalEditing(!internalEditing);
    }

    // If editing is provided, use external state
    if (external && !editing && onEditClick) {
      onEditClick(event);
    }
  };
  var onSaveButtonClick = function onSaveButtonClick(event) {
    if (internal && internalEditing) {
      onSaveClick(event);
      setInternalEditing(!internalEditing);
      return;
    }
    if (external && editing) {
      onSaveClick(event);
    }
  };
  var onCancelButtonClick = function onCancelButtonClick(event) {
    if (internal && internalEditing) {
      onCancelClick(event);
      setInternalEditing(!internalEditing);
    }
    if (external && editing) {
      onCancelClick(event);
    }
  };
  var renderedSubtitle = /*#__PURE__*/React.createElement(SubtitleComponent, {
    className: "cpc--card__subtitle"
  }, subtitle);
  var linkAttributes = linkProps || link && _defineProperty({}, linkProp, link);
  return /*#__PURE__*/React.createElement("div", _extends({
    className: classnames({
      'cpc--card__header': true,
      'cpc--card__header--editable': onSaveClick,
      'cpc--card__header--editing': editing || internalEditing,
      'cpc--card__header--small': small,
      'cpc--card__header--double': !small && subtitle
    }, className)
  }, rest), /*#__PURE__*/React.createElement("div", null, !small && subtitle && renderedSubtitle, /*#__PURE__*/React.createElement(TitleComponent, {
    className: "cpc--card__title"
  }, title), small && subtitle && renderedSubtitle), /*#__PURE__*/React.createElement("div", null, showButtons ?
  /*#__PURE__*/
  // eslint-disable-next-line react/jsx-no-useless-fragment
  React.createElement(React.Fragment, null, isEditing ? /*#__PURE__*/React.createElement(CardHeaderActions, {
    cancelDisabled: cancelDisabled,
    external: external,
    internal: internal,
    onCancelClick: onCancelClick,
    onCancelButtonClick: onCancelButtonClick,
    onSaveButtonClick: onSaveButtonClick,
    saveDisabled: saveDisabled,
    saveText: saveText,
    t: t
  }) : /*#__PURE__*/React.createElement(Button, {
    kind: "ghost",
    className: "cpc--card__header-button cpc--card__header-button--edit",
    onClick: onEditButtonClick,
    disabled: editDisabled
  }, editText || t('Edit'), /*#__PURE__*/React.createElement(Edit, {
    className: "cds--btn__icon"
  }))) : children, linkAttributes &&
  /*#__PURE__*/
  // eslint-disable-next-line react/jsx-props-no-spreading
  React.createElement(LinkElement, linkAttributes, linkIcon ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: "link-with-icon"
  }, linkText), linkIcon) : linkText || 'Icon')));
};
CardHeader.propTypes = {
  /**
   * Any children of the CardHeader. Actionable items should be passed into the header as children,
   * as the component or element will be rendered on the right hand side of the header.
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]),
  /**
   * Whether the cancel button is disabled.
   */
  cancelDisabled: PropTypes.bool,
  /**
   * Class name(s) to apply to the CardHeader component's root element.
   */
  className: PropTypes.string,
  /**
   * Alternative edit text to be rendered inside of the edit button.
   */
  editText: PropTypes.string,
  /**
   * If editing is set to true then the component will display the blue header and the save button.
   * This can be useful if the mechanism  to edit the card is located in an overflow menu rendered as a child of the CardHeader or triggering an edit mode needs to take
   * place outside of the CardHeader component.
   */
  editing: PropTypes.bool,
  /**
   * Whether the edit button is currently disabled.
   */
  editDisabled: PropTypes.bool,
  /**
   * An event handler to fire off when clicking the cancel button. The cancel button will only be
   * rendered if the user clicks the edit button or editing is set to true.
   */
  onCancelClick: PropTypes.func,
  /**
   * An event handler to fire off when clicking on the edit button. The edit button will only be
   * rendered if a onSaveClick is also provided to the CardHeader component.
   */
  onEditClick: PropTypes.func,
  /**
   * An event handler to fire off when clicking the save button. The save button will only be
   * rendered if the user clicks the edit button or editing is set to true.
   */
  onSaveClick: PropTypes.func,
  /**
   * Whether the save button is currently disabled.
   */
  saveDisabled: PropTypes.bool,
  /**
   * Alternative save text to be rendered inside of the save button.
   */
  saveText: PropTypes.string,
  /**
   * Whether or not to use the small header.
   */
  small: PropTypes.bool,
  /**
   * The element or component to render for the subtitle.
   */
  subtitle: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * The element or component to render for the subtitle.
   */
  subtitleComponent: PropTypes.elementType,
  /**
   * The title to be used for the Card component.
   */
  title: PropTypes.node.isRequired,
  /**
   * A component or element to be wrapped around the title. This is useful if you need to control
   * what heading element to represent the Card.
   */
  titleComponent: PropTypes.elementType,
  /**
   * A url to this card can link to for the user to get more information.
   */
  link: PropTypes.string,
  /**
   * Define what linkElement is used, default is an anchor tag, but if you need react router you can provide a Link element
   */
  linkElement: PropTypes.elementType,
  /**
   * Prop that the link should use. Defaults to href for anchor tags, but if you are using react router you can set this to 'to'
   */
  linkProp: PropTypes.string,
  /**
   * The text of the link
   */
  linkText: PropTypes.string,
  /**
   * The Icon to use next to the link
   */
  linkIcon: PropTypes.element,
  /**
   * Additional props for the link element
   */
  // eslint-disable-next-line react/forbid-prop-types
  linkProps: PropTypes.any
};
var CardHeader$1 = CardHeader;

export { CardHeader$1 as default };
